import { DEFAULT_APPEARANCE, applyCosmetics } from "@ghostland/shared";
import { session } from "../auth/session.js";
import { balanceStore } from "../items/balanceStore.js";
import { statsStore } from "../items/statsStore.js";
import { composeBust } from "../sprites/portrait.js";
import { injectTheme } from "./theme.js";
import { injectBillStyle, cashIcon, bankIcon } from "./pixelBills.js";

const STYLE_ID = "ghostland-money-style";

const CSS = `
#gl-money {
  position: fixed; top: 14px; left: 14px; z-index: 35;
  display: flex; align-items: center; gap: 10px;
  padding: 7px 13px 7px 7px;
  background: var(--gl-panel); border: 1px solid var(--gl-border); border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.6);
  color: var(--gl-text); user-select: none;
}
#gl-money .gl-portrait {
  width: 52px; height: 65px; image-rendering: pixelated; object-fit: cover;
  background: var(--gl-panel-2); border: 1px solid var(--gl-border); border-radius: 6px;
}
#gl-money .wallet { display: flex; flex-direction: column; gap: 3px; line-height: 1.1; }
#gl-money .name {
  font-size: 16px; font-weight: 700; color: var(--gl-text); letter-spacing: 0.02em;
  max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
#gl-money .hpbar {
  position: relative; width: 140px; height: 8px; border-radius: 4px; overflow: hidden;
  background: #1c1c1c; border: 1px solid var(--gl-border);
}
#gl-money .hpfill { position: absolute; inset: 0; width: 100%; background: linear-gradient(#4ee06e, #2bb84f); }
#gl-money .hptext {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font-size: 8px; color: #fff; font-weight: 700;
}
#gl-money .cash { font-size: 14px; font-weight: 700; }
#gl-money .cash .coin { color: var(--gl-muted); }
#gl-money .bank { font-size: 12px; font-weight: 700; color: var(--gl-muted); display: inline-flex; align-items: center; gap: 5px; }
`;

function ensureStyle(): void {
  injectTheme();
  injectBillStyle();
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

let el: HTMLDivElement | null = null;

/** Refresh the portrait + name from the current session character. */
function refreshIdentity(): void {
  if (!el) return;
  el.querySelector<HTMLSpanElement>(".name")!.textContent = session.character?.name ?? "";
  const c = session.character;
  const gear = c?.appearance ?? { ...DEFAULT_APPEARANCE };
  // Bust shows the DISPLAY appearance (gear + cosmetic overlay), like everyone sees us in-world.
  const appearance = (c?.showCosmetics ?? true)
    ? applyCosmetics(gear, { cosHat: c?.cosHat ?? "", cosTop: c?.cosTop ?? "", cosBottom: c?.cosBottom ?? "", cosShoes: c?.cosShoes ?? "" })
    : gear;
  void composeBust(appearance).then((canvas) => {
    canvas.className = "gl-portrait";
    el?.querySelector(".gl-portrait")?.replaceWith(canvas);
  });
}

/**
 * Mount the top-left profile HUD (dressed bust portrait + name + health + live balance).
 * The DOM is created once; identity is refreshed on every call so switching characters
 * updates the portrait/name without re-subscribing the balance.
 */
export function mountMoneyHud(): void {
  ensureStyle();
  if (!el) {
    el = document.createElement("div");
    el.id = "gl-money";
    el.innerHTML = `
      <div class="gl-portrait"></div>
      <div class="wallet">
        <span class="name"></span>
        <div class="hpbar"><div class="hpfill"></div><span class="hptext"></span></div>
        <span class="cash">${cashIcon()} <span class="gl-cash-amount">0</span></span>
        <span class="bank">${bankIcon()} <span class="gl-bank-amount">0</span></span>
      </div>`;
    document.body.appendChild(el);

    const cash = el.querySelector<HTMLSpanElement>(".gl-cash-amount")!;
    balanceStore.subscribe((balance) => { cash.textContent = balance.toLocaleString(); });
    const fill = el.querySelector<HTMLDivElement>(".hpfill")!;
    const hptext = el.querySelector<HTMLSpanElement>(".hptext")!;
    const bank = el.querySelector<HTMLSpanElement>(".gl-bank-amount")!;
    statsStore.subscribe(({ stats, bankMoney }) => {
      const pct = stats.maxHealth > 0 ? Math.max(0, Math.min(1, stats.health / stats.maxHealth)) : 0;
      fill.style.width = `${(pct * 100).toFixed(0)}%`;
      hptext.textContent = `${Math.round(stats.health)} / ${Math.round(stats.maxHealth)}`;
      bank.textContent = bankMoney.toLocaleString();
    });
  }
  refreshIdentity();
}
